import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import cx from 'classnames';
import { uniqueId } from 'lodash';
import { I18n } from 'common/i18n';
import { DragHandleIcon } from './DragHandleIcon';
import { DragDropElementWrapper } from './index';
import { getSortableStyle } from './sortableStyles';

const scope = 'common';

interface SortableTableElementProps<T = unknown> {
  item: DragDropElementWrapper<T>;
  isDragOverlay?: boolean;
  iconName?: string;
}

export const SortableTableElement = <T = unknown,>({
  item,
  isDragOverlay = false,
  iconName = 'drag_indicator',
}: SortableTableElementProps<T>) => {
  // Generate a stable default ID if none provided
  const sortableId = useMemo(() => item.id || uniqueId('sortable-table-'), [item.id]);
  const ariaLabel = I18n.t('drag_to_reorder_aria_label', { scope });

  // Use sortable for items that can be dragged and sorted
  const sortable = useSortable({
    id: sortableId,
    disabled: !item.dragAndDroppable, // Disable dragging if not draggable
    data: {
      type: 'sortable-item',
      item: item,
    },
  });
  const canDrop = item.dragAndDroppable || item.droppable;

  // Use droppable for items that can accept drops (drop zones)
  const droppable = useDroppable({
    id: `droppable-${sortableId}`,
    disabled: !canDrop,
    data: {
      accepts: ['sortable-item'],
      item: item,
    },
  });

  const { attributes, listeners, setNodeRef: setSortableRef, transform, transition, isDragging } = sortable;

  const { setNodeRef: setDroppableRef, isOver } = droppable;

  // Combine refs if item is both sortable and droppable
  const combinedRef = (element: HTMLElement | null) => {
    if (canDrop) {
      setSortableRef(element);
      setDroppableRef(element);
    }
  };

  const style = getSortableStyle({
    transform,
    transition,
    isDragging,
    isDragOverlay,
    additionalStyles: {
      padding: isDragOverlay ? '10px' : '',
    },
  });

  // Determine classes based on state
  const rowClasses = cx('drag-element', item.className, { dragging: isDragging && !isDragOverlay });

  return (
    <tr {...item.additionalItemProps} className={rowClasses} ref={combinedRef} style={style}>
      <td
        className={cx('drag-handle', { disabled: !item.dragAndDroppable })}
        {...(item.dragAndDroppable ? attributes : {})}
        {...(item.dragAndDroppable ? listeners : {})}
        aria-label={item.dragAndDroppable ? ariaLabel : undefined}
      >
        <DragHandleIcon name={iconName} slot="leading" isDraggable={item.dragAndDroppable} />
      </td>
      {item.element}
    </tr>
  );
};
